import { AfterViewInit, Component, inject } from '@angular/core';
import { FirebaseError } from '@angular/fire/app';
import { AngularFireModule } from "@angular/fire/compat";
import { AngularFireAuthModule } from "@angular/fire/compat/auth";
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTabChangeEvent, MatTabsModule } from '@angular/material/tabs';
import { Router } from '@angular/router';
import { createUserWithEmailAndPassword, getIdToken, GoogleAuthProvider, sendEmailVerification, signInWithEmailAndPassword, signInWithPopup } from 'firebase/auth';
import { environment } from '../../environments/environment';
import { appVersion } from '../app.version';
import { BaseComponent } from '../shared/base.component';
import { UserAccountUtil } from '../user-account/user-account-util';
import { UserAccountService } from '../user-account/user-account.service';
import { UnknownUser } from '../user/unknown-user.model';

@Component({
    selector: 'app-login',
    standalone: true,
    imports: [
        AngularFireModule,
        AngularFireAuthModule,
        MatTabsModule,
        ReactiveFormsModule,
        MatInputModule,
        MatButtonModule,
        MatIcon
    ],
    templateUrl: './login.component.html',
    styleUrl: './login.component.css'
})

export class LoginComponent extends BaseComponent implements AfterViewInit {

    private _router: Router = inject(Router);

    private userAccountService: UserAccountService = inject(UserAccountService);

    protected showPassword: boolean = false;

    private _email: FormControl = new FormControl("");
    private _password: FormControl = new FormControl("");

    public constructor() {
        super();
        console.log(`Application info: ${environment.firebase.projectId} - ${appVersion}`)
    }

    public ngAfterViewInit(): void {
        this.stateManager.setUserAccount(new UnknownUser())
    }

    public get password(): FormControl {
        return this._password;
    }

    public set password(event: Event) {
        this._password.setValue((event.target as HTMLInputElement).value);
    }

    public get email(): FormControl {
        return this._email;
    }

    public set email(event: Event) {
        this._email.setValue((event.target as HTMLInputElement).value);
    }

    public togglePasswordVisibility() {
        this.showPassword != this.showPassword;
    }

    public onTabClick(event: MatTabChangeEvent) {
        this.showPassword = false;
        super.message = "";
    }

    public async signup() {
        super.message = "";

        try {
            const userCredential = await createUserWithEmailAndPassword(this.auth, this.email.value, this.password.value);
            sendEmailVerification(userCredential.user);
            const userAccount = new UnknownUser();

            if (userCredential.user.email != null) {
                userAccount.email = userCredential.user.email;
            }

            this.stateManager.setUserAccount(userAccount);
            this._router.navigate(["/pending-verification"])

        } catch (error: any) {
            console.error(error);
            super.message = this.getAuthError(error);
        }
    }

    public async signInWithEmailPswd() {
        super.message = "";

        signInWithEmailAndPassword(this.auth, this.email.value, this.password.value).then(userCredential => {
            if (userCredential.user.emailVerified == true) {
                this.loadUserAccount();

            } else {
                const userAccount = new UnknownUser();

                if (userCredential.user.email != null) {
                    userAccount.email = userCredential.user.email;
                }

                this.stateManager.setUserAccount(userAccount);
                this._router.navigate(["/pending-verification"])
            }
        }).catch((error) => {
            console.error(error);
            super.message = this.getAuthError(error);
        });
    }

    public async signInWithGoogle() {
        super.message = "";

        signInWithPopup(this.auth, new GoogleAuthProvider()).then(() => {
            this.loadUserAccount();
        }).catch((error) => {
            if (error.code !== "auth/popup-closed-by-user") {
                console.error(error);
                super.message = this.getAuthError(error);
            }
        });
    }

    private async loadUserAccount() {
        const currentUser = this.auth.currentUser;

        if (currentUser) {
            const observable = this.userAccountService.getUserAccount(await getIdToken(currentUser));

            observable.subscribe(response => {
                try {
                    const json = JSON.parse(JSON.stringify(response));

                    if (json["get-user-account"] == "success") {
                        if (json["email-verified"]) {
                            const userAccount = UserAccountUtil.createUserAcountInstance(json["user-type"], json["user-account"]);
                            this.stateManager.setUserAccount(userAccount);

                            if (!(userAccount instanceof (UnknownUser))) {
                                this._router.navigate(["/home"])
                            } else {
                                this._router.navigate(["/unknown"])
                            }
                        } else {
                            // We're treating email not verified as an error because
                            // the expectation is that we should not get into this
                            // function if the email is pending verification.
                            throw new Error("Email not verified error");
                        }
                    } else {
                        throw new Error(json["message"]);
                    }
                } catch (error: any) {
                    console.error(error);
                    super.message = this.getAuthError(error);
                }
            })
        }
    }

    private getAuthError(error: Error) {
        if (error instanceof FirebaseError) {
            switch (error.code) {
                case "auth/invalid-credential":
                    return "Invalid email or password";
                case "auth/too-many-requests":
                    return "Too many requests with invalid email or password. Please try again in a few minutes.";
                case "auth/user-disabled":
                    return "Account disabled";
                case "auth/invalid-email":
                    return "Invalid email format";
                case "auth/email-already-in-use":
                    return "Email already in use";
                case "auth/password-does-not-meet-requirements":
                    const startIndex = error.message.indexOf("[") + 1;
                    const endIndex = error.message.indexOf("]");
                    return `${error.message.substring(startIndex, endIndex)}.`;
                case "auth/popup-closed-by-user":
                    return "";
                default:
                    console.error(`${error.code} - ${error.message}`);
                    return error.code;
            }
        } else {
            console.error(`${error.name} - ${error.message}`);
            return error.message;
        }
    }
}
